import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { DatePickerInput, DatesRangeValue } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { Select, Skeleton } from "@mantine/core";
import "@mantine/core/styles.css";
import dayjs from "dayjs";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { ISmallEventProps } from "../../types";
import { IOrganizationProps } from "../../types/Organization";
import { getEventUrl } from "../../lib/utils/domain";
import countriesWithCitiesData from "../../lib/data/countries_with_cities/countries.json";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Search,
  Filter,
  X,
  Calendar,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  isCustomDomain,
  getOrganizationSlugFromSubdomain,
  getDomainForLookup,
  isSubdomain,
} from "../../lib/utils/domain";
import { Navbar } from "../../components/Navbar";
import TheDRANavbar from "../../components/TheDRANavbar";
import { TheDRALogo } from "../../components/TheDRANavbar/TheDRALogo";
import { Login } from "../../components/Login";
import { AnimatePresence } from "../../components/AnimatePresenceWrapper";
import "../OrganizationHomepageTheDRA/styles.scss";

// Type for countries with cities data
type CountriesWithCities = Record<string, string[]>;

const EventsPage: React.FC = () => {
  const { organizationSlug: urlSlug } = useParams();
  const subdomainSlug = getOrganizationSlugFromSubdomain();
  const isOnSubdomain = isSubdomain();
  const usingCustomDomain = isCustomDomain();
  const domainForLookup = getDomainForLookup();

  // Get organization slug from subdomain or URL params
  const organizationSlug = subdomainSlug || urlSlug;

  // Choose the appropriate endpoint to fetch organization data
  const organizationEndpoint = useMemo(() => {
    if (usingCustomDomain && !isOnSubdomain) {
      return Endpoints.GetOrganizationUsingWebsite(domainForLookup);
    } else {
      if (!organizationSlug) {
        return null;
      }
      return Endpoints.GetSingleOrganization(organizationSlug);
    }
  }, [usingCustomDomain, isOnSubdomain, domainForLookup, organizationSlug]);

  // Fetch organization data to get organization ID
  const { data: organizationData, isLoading: isLoadingOrg } =
    usePerformRequest<{
      organization: IOrganizationProps;
    }>({
      method: organizationEndpoint?.method || "GET",
      route: organizationEndpoint?.route || "",
      doNotUseAuthorization: true,
    });

  // Extract organization object
  const organization = useMemo(() => {
    if (!organizationData) return null;
    const raw: any = organizationData;
    return raw?.data?.organization || raw?.organization || raw?.data;
  }, [organizationData]);

  // Extract organization ID
  const organizationId = useMemo(() => {
    if (!organization) return null;
    return organization?.id ? organization.id.toString() : null;
  }, [organization]);

  // Check if it's TheDRA
  const isTheDRA = useMemo(() => {
    if (!organization) return false;
    return (
      organizationSlug?.toLowerCase() === "thedra" ||
      organization?.slug?.toLowerCase() === "thedra" ||
      organization?.name?.toLowerCase().includes("digital risk alliance")
    );
  }, [organization, organizationSlug]);

  // Get homepage URL for TheDRANavbar
  const homepageUrl = useMemo(() => {
    if (usingCustomDomain && !isOnSubdomain) {
      return "/";
    }
    if (organizationSlug) {
      return `/organization/${organizationSlug}`;
    }
    return "/";
  }, [usingCustomDomain, isOnSubdomain, organizationSlug]);

  // Login modal state
  const [showLogin, setShowLogin] = useState(false);

  // Load countries with cities data
  const countriesWithCities = countriesWithCitiesData as CountriesWithCities;
  const countries = useMemo(() => {
    const uniqueCountries = Array.from(
      new Set(Object.keys(countriesWithCities))
    );
    return uniqueCountries.sort();
  }, [countriesWithCities]);

  // Filter states (raw input values)
  const [nameInput, setNameInput] = useState<string>("");
  const [categoryInput, setCategoryInput] = useState<string>("");
  const [locationCityInput, setLocationCityInput] = useState<string | null>("");

  // Debounced filter states (used for API requests)
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<string | null>("");
  const [category, setCategory] = useState<string>("");
  const [locationCity, setLocationCity] = useState<string>("");
  const [locationCountry, setLocationCountry] = useState<string | null>("");
  const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null]);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Debounce text inputs (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setName(nameInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCategory(categoryInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [categoryInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLocationCity(locationCityInput || "");
    }, 500);
    return () => clearTimeout(timer);
  }, [locationCityInput]);

  // Get cities for selected country
  const availableCities = useMemo(() => {
    if (!locationCountry) return [];
    const cities = countriesWithCities[locationCountry] || [];
    // Use Set to remove duplicates
    return Array.from(new Set(cities)).sort();
  }, [locationCountry, countriesWithCities]);

  // Reset city when country changes
  useEffect(() => {
    setLocationCityInput(null);
    setLocationCity("");
  }, [locationCountry]);

  // Format date to DD-MM-YYYY format as expected by API
  const formatDateForAPI = (date: Date | null | string): string | undefined => {
    if (!date) return undefined;
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return undefined;
    return dayjs(dateObj).format("D-MM-YYYY");
  };

  // Build filters object
  // Only include date range if BOTH dates are selected
  const filters = useMemo(() => {
    const filterParams: Parameters<typeof Endpoints.GetFilterEvents>[1] = {
      page,
      page_size: pageSize,
    };

    if (name.trim()) filterParams.name = name.trim();
    if (type) filterParams.type = type as "online" | "offline" | "hybrid";
    if (category.trim()) filterParams.category = category.trim();
    if (locationCity.trim()) filterParams.location_state = locationCity.trim();
    if (locationCountry && locationCountry.trim())
      filterParams.location_country = locationCountry.trim();
    // Only add date range if BOTH dates are selected
    if (dateRange[0] && dateRange[1]) {
      filterParams.start_date = formatDateForAPI(dateRange[0]);
      filterParams.end_date = formatDateForAPI(dateRange[1]);
    }

    return filterParams;
  }, [
    name,
    type,
    category,
    locationCity,
    locationCountry,
    dateRange,
    page,
    pageSize,
  ]);

  // Fetch events using GetFilterEvents
  const endpoint = useMemo(() => {
    if (!organizationId) {
      return { method: "GET" as const, route: "" };
    }
    return Endpoints.GetFilterEvents(organizationId, filters);
  }, [organizationId, filters]);

  const { data: eventsData, isLoading } = usePerformRequest<{
    events: ISmallEventProps[];
    count?: number;
    totalRecords?: number;
  }>({
    method: endpoint.method,
    route: endpoint.route,
    doNotUseAuthorization: true,
  });

  // Extract events from response
  const events = useMemo((): ISmallEventProps[] => {
    if (!eventsData) return [];
    const raw: any = eventsData;
    if (raw?.data?.events) return raw.data.events;
    if (raw?.events) return raw.events;
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    return [];
  }, [eventsData]);

  const totalRecords = useMemo(() => {
    if (!eventsData) return 0;
    const raw: any = eventsData;
    return raw.count || raw.totalRecords || events.length;
  }, [eventsData, events.length]);

  const totalPages = Math.ceil(totalRecords / pageSize);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [name, type, category, locationCity, locationCountry, dateRange]);

  // Clear all filters
  const handleClearFilters = () => {
    setNameInput("");
    setName("");
    setType(null as any);
    setCategoryInput("");
    setCategory("");
    setLocationCityInput(null as any);
    setLocationCity("");
    setLocationCountry(null as any);
    setDateRange([null, null]);
    setPage(1);
  };

  // Format time from start_time and end_time
  const formatEventTime = (startTime?: string, endTime?: string): string => {
    if (!startTime) return "";
    const formatTime = (timeString: string): string => {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };
    const formattedStart = formatTime(startTime);
    const formattedEnd = endTime ? formatTime(endTime) : null;
    return formattedEnd
      ? `${formattedStart} - ${formattedEnd}`
      : formattedStart;
  };

  // Get location string based on event type
  const getEventLocation = (eventType?: string): string => {
    switch (eventType) {
      case "online":
        return "Online Event";
      case "hybrid":
        return "Hybrid Event";
      case "offline":
        return "Venue TBA";
      default:
        return "Location TBA";
    }
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      nameInput.trim() !== "" ||
      type !== null ||
      categoryInput.trim() !== "" ||
      (locationCityInput !== null && locationCityInput.trim() !== "") ||
      locationCountry !== null ||
      dateRange[0] !== null ||
      dateRange[1] !== null
    );
  }, [
    nameInput,
    type,
    categoryInput,
    locationCityInput,
    locationCountry,
    dateRange,
  ]);

  // Event Card Skeleton Component
  const EventCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <Skeleton height={192} radius="md" />
      <div className="p-6">
        <Skeleton height={28} width="75%" radius="md" mb={8} />
        <Skeleton height={16} width="100%" radius="md" mb={4} />
        <Skeleton height={16} width="83%" radius="md" mb={16} />
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton height={16} width={16} circle />
            <Skeleton height={16} width={128} radius="md" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton height={16} width={16} circle />
            <Skeleton height={16} width={96} radius="md" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton height={16} width={16} circle />
            <Skeleton height={16} width={112} radius="md" />
          </div>
          <div className="mt-2">
            <Skeleton height={20} width={80} radius="md" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      {isTheDRA ? (
        <TheDRANavbar
          onLoginClick={() => setShowLogin(true)}
          organizationSlug={organization?.slug || "thedra"}
          showHomeLink={true}
          homepageUrl={homepageUrl}
        />
      ) : (
        organization && (
          <Navbar
            organizationName={organization.name || ""}
            logoImage={organization.logo || ""}
            onLoginClick={() => setShowLogin(true)}
          />
        )
      )}

      {/* Header */}
      <div
        className={`bg-white border-b border-gray-200 sticky top-0 z-10 ${
          !isTheDRA ? "pt-20" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">All Events</h1>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 overflow-hidden transition-all duration-300">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  Active
                </span>
              )}
            </div>
            {showFilters ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {showFilters && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Event Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Event Name
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search by event name..."
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Event Type */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Event Type
                    </label>
                    <Select
                      value={type}
                      onChange={(value) => setType(value || null)}
                      placeholder="Select type"
                      clearable
                      data={[
                        { value: "online", label: "Online" },
                        { value: "offline", label: "Offline" },
                        { value: "hybrid", label: "Hybrid" },
                      ]}
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter category..."
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                    />
                  </div>

                  {/* Location Country */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Country
                    </label>
                    <Select
                      value={locationCountry}
                      onChange={(value) => setLocationCountry(value || null)}
                      placeholder="Select country"
                      clearable
                      searchable
                      data={countries.map((country) => ({
                        value: country,
                        label: country,
                      }))}
                    />
                  </div>

                  {/* Location City */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      City
                    </label>
                    <Select
                      value={locationCityInput}
                      onChange={(value) => setLocationCityInput(value || null)}
                      placeholder={
                        locationCountry
                          ? "Select city"
                          : "Select a country first"
                      }
                      clearable
                      searchable
                      disabled={!locationCountry}
                      data={availableCities.map((city) => ({
                        value: city,
                        label: city,
                      }))}
                    />
                  </div>

                  {/* Date Range */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Date Range
                    </label>
                    <DatePickerInput
                      type="range"
                      placeholder="Pick dates range"
                      value={dateRange}
                      onChange={setDateRange}
                      clearable
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Section */}
        {isLoadingOrg || !organizationId || isLoading ? (
          <>
            {/* Results Count Skeleton */}
            <div className="mb-4">
              <Skeleton height={16} width={192} radius="md" />
            </div>
            {/* Events Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(4)].map((_, index) => (
                <EventCardSkeleton key={index} />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {events.length} of {totalRecords} events
            </div>

            {/* Events Grid */}
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event: ISmallEventProps) => {
                  const eventDate = event.date
                    ? new Date(event.date).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Date TBA";
                  const eventTime = formatEventTime(
                    event.start_time,
                    event.end_time
                  );
                  const eventLocation = getEventLocation(event.type);
                  const eventUrl = getEventUrl(
                    event.id.toString(),
                    organizationSlug || undefined
                  );

                  return (
                    <Link
                      key={event.id}
                      to={eventUrl}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {event.cover_image && (
                        <div className="w-full h-48 overflow-hidden">
                          <img
                            src={event.cover_image}
                            alt={event.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                          {event.name}
                        </h3>
                        {event.short_description && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {event.short_description}
                          </p>
                        )}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>{eventDate}</span>
                          </div>
                          {eventTime && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>{eventTime}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{eventLocation}</span>
                          </div>
                          {event.category && (
                            <div className="mt-2">
                              <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                {event.category}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-600 text-lg">
                  No events found matching your filters.
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    className="mt-4"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <div className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {isTheDRA && organization && (
        <footer className="thedra-footer">
          <div className="thedra-container">
            <div className="thedra-footer-content">
              <div className="thedra-footer-left">
                <div className="thedra-footer-logo">
                  <TheDRALogo />
                </div>
                <p className="thedra-footer-email">
                  {organization.email || "info@TheDRA.org"}
                </p>
                <a
                  href={organization.linkedin_link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="thedra-footer-linkedin"
                >
                  in
                </a>
              </div>
              <div className="thedra-footer-right">
                <div className="thedra-footer-links">
                  <a href="#privacy" className="thedra-footer-link">
                    Privacy Policy
                  </a>
                  <a href="#accessibility" className="thedra-footer-link">
                    Accessibility Statement
                  </a>
                </div>
                <p className="thedra-footer-copyright">
                  © {new Date().getFullYear()} by{" "}
                  {organization.name || "The Digital Risk Alliance (TheDRA)"}.
                </p>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && organization && (
          <Login
            onClose={() => setShowLogin(false)}
            organizationName={organization.name || ""}
            organizationId={organization.id || 0}
            organizationSlug={organizationSlug || organization.slug || ""}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventsPage;
